import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, Popover } from '@mui/material';
import { Typography, IconButton } from '@totalsoft/rocket-ui';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { ArrowBack, Assignment, Print, Timeline, AccountTree, List, Remove, DoneAll, CompareArrows } from '@mui/icons-material';
import FolderStatesSelectionListContainer from '../../edit/states/components/FolderStatesSelectionListContainer';
import { emptyString } from 'constants/emptyConst';

const FolderHeader = ({
  documentId,
  siteId,
  t,
  data,
  isImage,
  onChangeActiveStep,
  history,
  loadFolderReports,
  onChangeShowReportsModal,
  folderWithChecklist,
  viewWorkflow,
  documentPropertiesRead,
  documentReportsRead,
  documentChecklistRead,
  openFolderActions,
  handleCloseFolderActions,
  handleOpenFolderActions,
  clientVerificationsRead,
  otherStateTransitionHistoryRead,
  isFacilityApproval,
  anchorEl
}) => {
  const defaultGridActions = 3;
  const defaultGridSpace = 3;

  const onCloseClick = useCallback(() => {
    const urlFilters = sessionStorage.getItem('FolderURLFilters');
    sessionStorage.removeItem('FolderURLFilters');
    history.push('/folders' + (urlFilters ?? emptyString));
  }, [history]);

  const onReportsClick = useCallback(() => {
    onChangeShowReportsModal(true);
    loadFolderReports(documentId, siteId);
  }, [documentId, loadFolderReports, onChangeShowReportsModal, siteId]);

  const onPrintClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/documents`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onVerificationsClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/verifications`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onPropertiesClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/document-properties`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onStateTransitionsClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/state-transitions`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onCheckListClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/checklist`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const onWorkflowClick = useCallback(() => {
    onChangeActiveStep(-1);
    history.push(`/folders/${documentId}/${siteId}/workflows`);
  }, [documentId, history, onChangeActiveStep, siteId]);

  const getNumberOfGridActions = useCallback(() => {
    return defaultGridActions + !isImage + folderWithChecklist + data.hasProperties + viewWorkflow + clientVerificationsRead;
  }, [clientVerificationsRead, data.hasProperties, folderWithChecklist, isImage, viewWorkflow]);

  const titleKey = isImage
    ? isFacilityApproval
      ? 'Folder.FacilityApprovalImagePageTitle'
      : 'Folder.ImagePageTitle'
    : isFacilityApproval
    ? 'Folder.FacilityApprovalPageTitle'
    : 'Folder.PageTitle';

  return (
    <Grid container alignItems='center' justifyContent='space-between'>
      {isImage ? (
        <Typography fontWeight='bold' variant='subtitle1'>
          {t(titleKey, {
            data: { ...data, folderDate: t('DATE_FORMAT', { date: { value: data.folderDate, format: 'DD/MM/YYYY' } }) }
          })}
        </Typography>
      ) : (
        <Grid item xs alignItems='center' display='flex' gap={2} flexShrink={1} minWidth={0}>
          <Typography fontWeight='bold' variant='subtitle1' noWrap>
            {t(titleKey, { data: data })}
          </Typography>
          <FolderStatesSelectionListContainer documentId={documentId} siteId={siteId} />
        </Grid>
      )}
      <Grid item container xs='auto' justifyContent='end'>
        <IconButton tooltip={t('Folder.Action.BackToList')} onClick={onCloseClick} variant='text' color='primary'>
          <ArrowBack />
        </IconButton>
        {!isImage && (
          <IconButton tooltip={t('Folder.Action.Print')} onClick={onPrintClick} variant='text' color='primary'>
            <Print />
          </IconButton>
        )}

        <Popover
          open={openFolderActions}
          anchorEl={anchorEl}
          onClose={handleCloseFolderActions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <Grid container spacing={2} p={1} justifyContent='center'>
            {documentReportsRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.Reports')}
                  onClick={onReportsClick}
                  id={'folderHeader_actionList_reports'}
                  variant='text'
                  color='primary'
                >
                  <Assignment />
                </IconButton>
              </Grid>
            )}
            {documentPropertiesRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.Properties')}
                  onClick={onPropertiesClick}
                  id={'folderHeader_actionList_properties'}
                  variant='text'
                  color='primary'
                >
                  <AccountTree />
                </IconButton>
              </Grid>
            )}
            {otherStateTransitionHistoryRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.StateTransitions')}
                  onClick={onStateTransitionsClick}
                  id={'folderHeader_actionList_stateTransitions'}
                  variant='text'
                  color='primary'
                >
                  <CompareArrows />
                </IconButton>
              </Grid>
            )}
            {folderWithChecklist && documentChecklistRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.CheckList')}
                  onClick={onCheckListClick}
                  id={'folderHeader_actionList_checklist'}
                  variant='text'
                  color='primary'
                >
                  <PlaylistAddCheckIcon />
                </IconButton>
              </Grid>
            )}
            {!isImage && viewWorkflow && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.Workflow')}
                  onClick={onWorkflowClick}
                  id={'folderHeader_actionList_workflow'}
                  variant='text'
                  color='primary'
                >
                  <Timeline />
                </IconButton>
              </Grid>
            )}
            {clientVerificationsRead && (
              <Grid item xs={getNumberOfGridActions() > defaultGridActions ? defaultGridSpace : 12 / getNumberOfGridActions()}>
                <IconButton
                  tooltip={t('Folder.Action.Verifications')}
                  onClick={onVerificationsClick}
                  id={'folderHeader_actionList_verifications'}
                  variant='text'
                  color='primary'
                >
                  <DoneAll />
                </IconButton>
              </Grid>
            )}
          </Grid>
        </Popover>

        {!openFolderActions ? (
          <IconButton
            tooltip={t('Tooltips.ShowHeaderActions')}
            onClick={handleOpenFolderActions}
            id={'folderHeader_actionList_showBtn'}
            variant='text'
            color='primary'
          >
            <List />
          </IconButton>
        ) : (
          <IconButton
            tooltip={t('Tooltips.HideHeaderActions')}
            onClick={handleCloseFolderActions}
            id={'folderHeader_actionList_hideBtn'}
            variant='text'
            color='primary'
          >
            <Remove />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
};

FolderHeader.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  onChangeShowReportsModal: PropTypes.func.isRequired,
  loadFolderReports: PropTypes.func.isRequired,
  folderWithChecklist: PropTypes.bool.isRequired,
  isImage: PropTypes.bool,
  viewWorkflow: PropTypes.bool,
  openFolderActions: PropTypes.bool,
  handleOpenFolderActions: PropTypes.func,
  handleCloseFolderActions: PropTypes.func,
  clientVerificationsRead: PropTypes.bool,
  documentPropertiesRead: PropTypes.bool,
  documentReportsRead: PropTypes.bool,
  documentChecklistRead: PropTypes.bool,
  otherStateTransitionHistoryRead: PropTypes.bool,
  isFacilityApproval: PropTypes.bool,
  anchorEl: PropTypes.object
};

export default compose(withTranslation())(FolderHeader);
